import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { usePaginatedMarketingResources } from "creso-core/hooks";
import type {
  GetMarketingResourcesParams,
  MarketingResource,
} from "creso-core/types";

const DEFAULT_PAGE_SIZE = 10;

type UseInfiniteMarketingResourcesParams = Pick<
  GetMarketingResourcesParams,
  "resource_type" | "search" | "topic_ids"
> & {
  page_size?: number;
  listResetKey: string;
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
};

export function useInfiniteMarketingResources({
  resource_type,
  search,
  topic_ids,
  page_size = DEFAULT_PAGE_SIZE,
  listResetKey,
  scrollContainerRef: scrollContainerRefProp,
}: UseInfiniteMarketingResourcesParams) {
  const [page, setPage] = useState(1);
  const [resourcesAccumulated, setResourcesAccumulated] = useState<
    MarketingResource[]
  >([]);
  const [hasMore, setHasMore] = useState(false);
  const [prevListResetKey, setPrevListResetKey] = useState(listResetKey);

  if (prevListResetKey !== listResetKey) {
    setPrevListResetKey(listResetKey);
    setPage(1);
    setResourcesAccumulated([]);
    setHasMore(false);
  }

  const internalScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef =
    scrollContainerRefProp ?? internalScrollContainerRef;
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const {
    data: resourcesResponse,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = usePaginatedMarketingResources({
    resource_type,
    search,
    topic_ids,
    page,
    page_size,
  });

  const resourcesPage = useMemo(
    () => resourcesResponse?.results ?? [],
    [resourcesResponse]
  );
  const totalCount = resourcesResponse?.count ?? 0;

  useEffect(() => {
    if (!isLoading) {
      setHasMore(page * page_size < totalCount);
    }

    if (isPlaceholderData) return;

    if (page === 1) {
      setResourcesAccumulated(resourcesPage);
      return;
    }

    setResourcesAccumulated((prev) => {
      const byId = new Map(prev.map((resource) => [resource.id, resource]));
      resourcesPage.forEach((resource) => {
        byId.set(resource.id, resource);
      });
      return Array.from(byId.values());
    });
  }, [
    isLoading,
    isPlaceholderData,
    page,
    page_size,
    resourcesPage,
    totalCount,
  ]);

  useEffect(() => {
    observerRef.current?.disconnect();

    const root = scrollContainerRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasMore) return;
        if (isLoading || isFetching) return;
        setPage((currentPage) => currentPage + 1);
      },
      {
        root,
        rootMargin: "250px 0px",
        threshold: 0,
      }
    );

    observerRef.current = observer;
    observer.observe(sentinel);
  }, [hasMore, isFetching, isLoading, scrollContainerRef]);

  const isInitialLoading =
    page === 1 &&
    resourcesAccumulated.length === 0 &&
    (isLoading || isFetching);

  return {
    page,
    resourcesAccumulated,
    hasMore,
    isLoading,
    isFetching,
    isInitialLoading,
    scrollContainerRef,
    loadMoreSentinelRef,
  };
}
